import { apiClient } from "@/config/api.config";

const API_PREFIX = "/mutual-funds";

/**
 * Service for interacting with mutual fund related APIs
 */
class MutualFundService {
  /**
   * Get all available mutual fund schemes
   * @param {Object} params - Optional query parameters
   * @returns {Promise} - Promise with the response data
   */
  getAllSchemes(params = {}) {
    return apiClient.get(`${API_PREFIX}/`, { params });
  }

  /**
   * Search mutual fund schemes with various filters
   * @param {Object} filters - Search filters (name, fundHouse, category, type)
   * @returns {Promise} - Promise with the response data
   */
  searchSchemes(filters = {}) {
    return apiClient.get(`${API_PREFIX}/search`, {
      params: filters,
    });
  }

  /**
   * Get details of a specific mutual fund scheme
   * @param {string} schemeCode - The mutual fund scheme code
   * @returns {Promise} - Promise with the response data
   */
  getSchemeDetails(schemeCode) {
    return apiClient.get(`${API_PREFIX}/${schemeCode}`);
  }

  /**
   * Get NAV history for a specific mutual fund scheme
   * @param {string} schemeCode - The mutual fund scheme code
   * @param {Object} params - Parameters like startDate, endDate, limit
   * @returns {Promise} - Promise with the response data
   */
  getNAVHistory(schemeCode, params = {}) {
    return apiClient.get(`${API_PREFIX}/${schemeCode}/nav-history`, {
      params,
    });
  }

  /**
   * Calculate returns for a specific mutual fund scheme
   * @param {string} schemeCode - The mutual fund scheme code
   * @param {Object} params - Parameters like period
   * @returns {Promise} - Promise with the response data
   */
  calculateReturns(schemeCode, params = {}) {
    return apiClient.get(`${API_PREFIX}/${schemeCode}/returns`, {
      params,
    });
  }

  /**
   * Compare multiple mutual fund schemes
   * @param {Array} schemeCodes - Array of scheme codes to compare
   * @param {Array} periods - Optional array of time periods to compare
   * @returns {Promise} - Promise with the response data
   */
  compareSchemes(schemeCodes, periods = ["1y", "3y", "5y"]) {
    return apiClient.post(`${API_PREFIX}/compare`, {
      schemeCodes,
      periods,
    });
  }

  /**
   * Purchase mutual fund units
   * @param {string} schemeCode - The mutual fund scheme code
   * @param {number} amount - Amount to invest
   * @returns {Promise} - Promise with the response data
   */
  purchaseMutualFund(schemeCode, amount) {
    return apiClient.post(`${API_PREFIX}/purchase`, {
      schemeCode,
      amount,
    });
  }

  /**
   * Sell mutual fund units
   * @param {string} schemeCode - The mutual fund scheme code
   * @param {number} units - Number of units to sell
   * @returns {Promise} - Promise with the response data
   */
  sellMutualFund(schemeCode, units) {
    return apiClient.post(`${API_PREFIX}/sell`, {
      schemeCode,
      units,
    });
  }

  /**
   * Get user's mutual fund portfolio
   * @returns {Promise} - Promise with the response data
   */
  getUserHoldings() {
    try {
      // First check if there's a valid token
      const token = localStorage.getItem("authToken");
      if (!token) {
        return Promise.resolve({
          data: { success: false, message: "No authentication token found" },
        });
      }

      // Make the API call
      return apiClient.get("/portfolio/user/portfolios");
    } catch (error) {
      return Promise.resolve({
        data: { success: false, message: "Error fetching holdings" },
      });
    }
  }

  /**
   * Update mutual fund portfolio details
   * @param {string} portfolioId - ID of the portfolio to update
   * @param {Object} details - Portfolio details to update
   * @returns {Promise} - Promise with the response data
   */
  updatePortfolioDetails(portfolioId, details) {
    return apiClient.patch(`/portfolio/${portfolioId}`, details);
  }

  /**
   * Add a mutual fund to a watchlist
   * @param {Object} payload - Data including schemeCode, watchlistId, notes, and alertSettings
   * @returns {Promise} - Promise with the response data
   */
  /**
 * Adds a mutual fund to the user's watchlist
 * @param {Object} payload - The mutual fund data to add
 * @param {string} payload.schemeCode - The scheme code of the mutual fund
 * @returns {Promise} A promise that resolves to the API response
 * 
 * @example
 * // Example payload:
 * addMutualFundToWatchlist({
 *   "schemeCode": "113060"
 * });
 */
  addMutualFundToWatchlist(payload) {
    try {
      // Check for valid token
      const token = localStorage.getItem("authToken");
      if (!token) {
        return Promise.resolve({
          data: { success: false, message: "No authentication token found" },
        });
      }



      // Validate payload
      if (!payload || !payload.schemeCode) {
        return Promise.resolve({
          data: { success: false, message: "Invalid payload: schemeCode is required" },
        });
      }

      // Make the API call
      return apiClient.post(`${API_PREFIX}/watchlists/mutual-funds`, payload);
    } catch (error) {
      return Promise.resolve({
        data: { success: false, message: "Error adding mutual fund to watchlist" },
      });
    }
  }
}

export default new MutualFundService();